import type { RouteAccessRule } from './routeAccess.types';
import {
  MY_TXT_SESSION_USER_TYPES,
  ACTIVE_STUDENT_TYPES,
  SCLA_APPLICATION_USER_TYPES,
} from './myTxtUserTypes';
import {
  MOTHERSHIP_APP_READ_PERMISSIONS,
  MOTHERSHIP_APPS,
} from './mothershipApps.config';

export const DEFAULT_DENIED_REDIRECT = '/home';

/**
 * Route access rules — longest pathPrefix wins; empty prefix is the fallback.
 *
 * allowedUserTypes uses MY_TXT_SESSION_USER_TYPES (all portal types) by default.
 * To restrict a route to a subset, swap in ACTIVE_STUDENT_TYPES,
 * STUDENT_AND_ALUMNI_TYPES, etc. from myTxtUserTypes.ts.
 */
export const ROUTE_ACCESS_RULES: RouteAccessRule[] = [
  // All portal types — core navigation
  {
    id: 'home',
    pathPrefix: '/home',
    allowedUserTypes: MY_TXT_SESSION_USER_TYPES,
    readPermission: 'home:read',
  },
  {
    id: 'profile',
    pathPrefix: '/profile',
    allowedUserTypes: MY_TXT_SESSION_USER_TYPES,
    readPermission: 'profile:read',
  },
  {
    id: 'signed-forms',
    pathPrefix: '/forms',
    allowedUserTypes: MY_TXT_SESSION_USER_TYPES,
    readPermission: 'signed-forms:read',
  },
  {
    id: 'txt-training',
    pathPrefix: '/txt-training',
    allowedUserTypes: MY_TXT_SESSION_USER_TYPES,
    readPermission: 'txt-training:read',
  },
  {
    id: 'office-check-in',
    pathPrefix: '/office',
    allowedUserTypes: MY_TXT_SESSION_USER_TYPES,
    readPermission: 'office-check-in:read',
  },
  // All portal types — program access (narrow to ACTIVE_STUDENT_TYPES when ready)
  {
    id: 'scla-application',
    pathPrefix: '/scla-application',
    allowedUserTypes: SCLA_APPLICATION_USER_TYPES,
    readPermission: 'scla-application:read',
  },
  {
    id: 'scla-team-roster',
    pathPrefix: '/scla/team',
    allowedUserTypes: MY_TXT_SESSION_USER_TYPES,
    readPermission: 'scla-team-roster:read',
  },
  {
    id: 'txt-labs-apprenticeship',
    pathPrefix: '/txt-labs/apprenticeship',
    allowedUserTypes: MY_TXT_SESSION_USER_TYPES,
    readPermission: 'txt-labs-apprenticeship:read',
  },
  {
    id: 'seals',
    pathPrefix: '/programs/seals',
    allowedUserTypes: MY_TXT_SESSION_USER_TYPES,
    readPermission: 'seals:read',
  },
  {
    id: 'ai-image-chat',
    pathPrefix: '/ai-image-chat',
    allowedUserTypes: MY_TXT_SESSION_USER_TYPES,
    readPermission: 'home:read',
    publicAccess: true,
  },
  // SCLA → Mobile Application (under My Team). Access if the user has any
  // per-app *-app-admin:read grant (explicit-only; never a role default).
  // Restricted to active students. Page locks to the granted app (no switcher).
  {
    id: 'scla-mobile-applications',
    pathPrefix: '/scla/mobile-applications',
    allowedUserTypes: ACTIVE_STUDENT_TYPES,
    readPermission: `${MOTHERSHIP_APPS[0].resource}:read`,
    anyOfReadPermissions: MOTHERSHIP_APP_READ_PERMISSIONS,
  },
  // Fallback — matches any unrecognised path
  {
    id: 'student',
    pathPrefix: '',
    allowedUserTypes: MY_TXT_SESSION_USER_TYPES,
    readPermission: 'home:read',
  },
];

export const STUDENT_FALLBACK_RULE: RouteAccessRule =
  ROUTE_ACCESS_RULES.find(r => r.id === 'student')!;
